import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, Button, Typography, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { adminRestaurantPosService } from "../../../services/adminService";

const { Text } = Typography;

interface TaxInvoiceModalProps {
  visible: boolean;
  orderId: string | null;
  bookingId?: string | null;
  guestName?: string;
  onCancel: () => void;
  onPrint: (orderId: string, customerDetails: {
    customerName: string;
    companyName?: string;
    phone?: string;
    address?: string;
    tinVatNumber?: string;
  }) => void;
}

export const TaxInvoiceModal: React.FC<TaxInvoiceModalProps> = ({
  visible,
  orderId,
  bookingId,
  guestName,
  onCancel,
  onPrint,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSearchResults([]);
      setSearchValue("");
      setShowResults(false);

      // Pre-fill guest name if available
      if (guestName) {
        form.setFieldsValue({ customerName: guestName });
      }

      // Fetch booking guest data if bookingId is available
      if (bookingId) {
        fetchBookingGuest();
      }
    }
  }, [visible, bookingId, guestName, form]);

  // Debounced search on each keystroke
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!searchValue || searchValue.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await adminRestaurantPosService.searchCustomers(searchValue);
        const data = result?.data || [];
        setSearchResults(data);
        setShowResults(data.length > 0);
      } catch {
        setSearchResults([]);
        setShowResults(false);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchValue]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    if (showResults) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResults]);

  const fetchBookingGuest = async () => {
    try {
      const result = await adminRestaurantPosService.getBookingGuest(bookingId!);
      if (result?.data) {
        const guest = result.data;
        form.setFieldsValue({
          customerName: guest.guest_name || guestName || "",
          phone: guest.phone || "",
          address: [guest.address_line_1, guest.address_line_2].filter(Boolean).join(", ") || "",
        });
      }
    } catch {
      // Silently fail — user can still enter manually
    }
  };

  const handleSelectCustomer = (customer: any) => {
    form.setFieldsValue({
      customerName: customer.full_name || "",
      companyName: customer.company_name || "",
      phone: customer.phone || "",
      address: [customer.address_line_1, customer.address_line_2].filter(Boolean).join(", ") || "",
      tinVatNumber: customer.tin_vat_number || "",
    });
    setSearchResults([]);
    setSearchValue("");
    setShowResults(false);
  };

  const handlePrint = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      onPrint(orderId!, {
        customerName: values.customerName,
        companyName: values.companyName || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined,
        tinVatNumber: values.tinVatNumber || undefined,
      });
      onCancel();
    } catch {
      // Validation failed
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Tax Invoice — Customer Details"
      open={visible}
      onCancel={onCancel}
      width={520}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="print" type="primary" loading={loading} onClick={handlePrint}>
          Print Tax Invoice
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        {/* Customer Search */}
        <div style={{ marginBottom: 16, position: "relative" }}>
          <Text strong style={{ display: "block", marginBottom: 4 }}>Search Existing Customer</Text>
          <Input
            placeholder="Type to search by name, phone, or company..."
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => {
              if (searchResults.length > 0) setShowResults(true);
            }}
            allowClear
          />
          {showResults && searchResults.length > 0 && (
            <div
              ref={resultsRef}
              style={{
                position: "absolute",
                zIndex: 1000,
                width: "100%",
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                maxHeight: 200,
                overflowY: "auto",
                backgroundColor: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
            >
              {searchResults.map((c: any) => (
                <div
                  key={c.id}
                  onClick={() => handleSelectCustomer(c)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  <div><Text strong>{c.full_name}</Text> {c.company_name && <Text type="secondary">({c.company_name})</Text>}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>{c.phone || "No phone"} {c.tin_vat_number && `• TIN: ${c.tin_vat_number}`}</div>
                </div>
              ))}
            </div>
          )}
          {showResults && searchResults.length === 0 && !searchLoading && searchValue.length >= 2 && (
            <div style={{
              position: "absolute",
              zIndex: 1000,
              width: "100%",
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              padding: "8px 12px",
              color: "#888",
            }}>
              No customers found
            </div>
          )}
        </div>

        {/* Customer Details Form */}
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="customerName" label="Customer Name" rules={[{ required: true, message: "Customer name is required" }]}>
            <Input placeholder="Enter customer name" />
          </Form.Item>
          <Form.Item name="companyName" label="Company Name">
            <Input placeholder="Enter company name (optional)" />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} placeholder="Enter address (optional)" />
          </Form.Item>
          <Form.Item name="tinVatNumber" label="TIN/VAT No">
            <Input placeholder="Enter TIN/VAT number" />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};
